// src/services/firebase.js
import { initializeApp } from 'firebase/app';
import { getDatabase, ref, get, set, child, update } from 'firebase/database';

// 🔥 Configuración DIRECTA (sin .env)
const firebaseConfig = {
  apiKey: "AIzaSyBtt81A-HHazfqV2DxTC9LPpyo4alse1TI",
  authDomain: "mmorelia.firebaseapp.com",
  databaseURL: "https://mmorelia-default-rtdb.firebaseio.com",
  projectId: "mmorelia",
  storageBucket: "mmorelia.firebasestorage.app",
  messagingSenderId: "383420803298",
  appId: "1:383420803298:web:3d9df7d9af767800de16bb",
  measurementId: "G-F15XML966Z"
};

// Inicializar Firebase
const app = initializeApp(firebaseConfig);
const db = getDatabase(app);

// ========== SERVICIOS ==========

// ===== OBTENER TODOS LOS MERCADOS =====
export const getMercados = async () => {
  try {
    const snapshot = await get(ref(db, 'mercados'));
    if (snapshot.exists()) {
      const data = snapshot.val();
      return Object.keys(data).map(key => {
        const mercado = data[key];
        return {
          id: key,
          ...mercado,
          fotosPortada: mercado.portadaConfig?.fotosPortada || []
        };
      });
    }
    return [];
  } catch (error) {
    console.error('Error obteniendo mercados:', error);
    return [];
  }
};

// Obtener un mercado por ID
export const getMercadoById = async (id) => {
  try {
    const snapshot = await get(ref(db, `mercados/${id}`));
    if (snapshot.exists()) {
      return { id, ...snapshot.val() };
    }
    return null;
  } catch (error) {
    console.error('Error obteniendo mercado:', error);
    return null;
  }
};

// Obtener configuración de portada de un mercado
export const getPortadaConfig = async (mercadoId) => {
  try {
    const snapshot = await get(ref(db, `mercados/${mercadoId}/portadaConfig`));
    if (snapshot.exists()) {
      return snapshot.val();
    }
    return {
      fotosPortada: [],
      modoOrden: 'aleatorio',
      fotosManuales: [],
      fotosDestacadas: [],
      mensajeBienvenida: {
        activo: false,
        titulo: '¡Bienvenido!',
        descripcion: '',
        imagen: ''
      }
    };
  } catch (error) {
    console.error('Error obteniendo configuración de portada:', error);
    return null;
  }
};

// Obtener notificación de un mercado
export const getNotificacion = async (mercadoId) => {
  try {
    const snapshot = await get(ref(db, `mercados/${mercadoId}/notificacion`));
    if (snapshot.exists()) {
      return snapshot.val();
    }
    return null;
  } catch (error) {
    console.error('Error obteniendo notificación:', error);
    return null;
  }
};

// Guardar notificación de un mercado
export const guardarNotificacion = async (mercadoId, data) => {
  try {
    const notificacionRef = ref(db, `mercados/${mercadoId}/notificacion`);
    await set(notificacionRef, data);
    return true;
  } catch (error) {
    console.error('Error guardando notificación:', error);
    return false;
  }
};

// Obtener locales de un mercado
export const getLocales = async (mercadoId) => {
  try {
    const snapshot = await get(ref(db, `mercados/${mercadoId}/locales`));
    if (snapshot.exists()) {
      const data = snapshot.val();
      return Object.keys(data).map(key => ({
        id: key,
        ...data[key]
      }));
    }
    return [];
  } catch (error) {
    console.error('Error obteniendo locales:', error);
    return [];
  }
};

// Obtener un local por ID
export const getLocalById = async (mercadoId, localId) => {
  try {
    const snapshot = await get(ref(db, `mercados/${mercadoId}/locales/${localId}`));
    if (snapshot.exists()) {
      return { id: localId, ...snapshot.val() };
    }
    return null;
  } catch (error) {
    console.error('Error obteniendo local:', error);
    return null;
  }
};

// Obtener locales destacados de un mercado
export const getLocalesDestacados = async (mercadoId, fotosDestacadas = []) => {
  try {
    const locales = await getLocales(mercadoId);
    return locales.filter(local => fotosDestacadas.includes(local.id));
  } catch (error) {
    console.error('Error obteniendo locales destacados:', error);
    return [];
  }
};

// ========== UTILIDADES ==========

// Obtener fotos de portada según el modo de orden
export const getFotosPortada = (config, fotosDisponibles) => {
  const { modoOrden, fotosManuales } = config;
  
  if (!fotosDisponibles || fotosDisponibles.length === 0) {
    return [];
  }

  switch (modoOrden) {
    case 'manual':
      return fotosDisponibles;
      
    case 'mixto':
      const manuales = fotosDisponibles.filter(f => fotosManuales.includes(f));
      const resto = fotosDisponibles.filter(f => !fotosManuales.includes(f));
      const restoAleatorio = resto.sort(() => Math.random() - 0.5);
      return [...manuales, ...restoAleatorio];
      
    case 'aleatorio':
      return [...fotosDisponibles].sort(() => Math.random() - 0.5);
      
    default:
      return fotosDisponibles;
  }
};

// Verificar si un local es destacado
export const isLocalDestacado = (localId, fotosDestacadas) => {
  return fotosDestacadas.includes(localId);
};

// Formatear horario
export const formatHorario = (horario) => {
  if (!horario || !horario.abierto) return 'Cerrado';
  return `${horario.apertura} - ${horario.cierre}`;
};

// Obtener día de la semana actual
export const getDiaActual = () => {
  const dias = ['domingo', 'lunes', 'martes', 'miercoles', 'jueves', 'viernes', 'sabado'];
  return dias[new Date().getDay()];
};

// ========== VISITAS ==========

// Registrar una visita en Firebase
export const registrarVisita = async () => {
  try {
    const hoy = new Date().toISOString().split('T')[0];
    const userKey = `visitante_${hoy}`;
    
    // Verificar si ya registró visita hoy (localStorage)
    if (localStorage.getItem(userKey)) {
      return false;
    }
    
    localStorage.setItem(userKey, 'true');
    
    // 1. Actualizar contador del día
    const visitaRef = ref(db, `visitas/${hoy}`);
    const snapshot = await get(visitaRef);
    
    let total = 0;
    if (snapshot.exists()) {
      total = snapshot.val().total || 0;
    }
    total += 1;
    
    await set(visitaRef, {
      total: total,
      ultimaActualizacion: new Date().toISOString()
    });
    
    // 2. Actualizar contador global
    const globalRef = ref(db, 'visitas/total_global');
    const globalSnap = await get(globalRef);
    let globalTotal = 0;
    if (globalSnap.exists()) {
      globalTotal = globalSnap.val() || 0;
    }
    globalTotal += 1;
    await set(globalRef, globalTotal);
    
    return true;
  } catch (error) {
    console.error('Error registrando visita:', error);
    return false;
  }
};

// Obtener visitas del día actual
export const getVisitasDelDia = async () => {
  try {
    const hoy = new Date().toISOString().split('T')[0];
    const snapshot = await get(ref(db, `visitas/${hoy}`));
    if (snapshot.exists()) {
      return snapshot.val().total || 0;
    }
    return 0;
  } catch (error) {
    console.error('Error obteniendo visitas del día:', error);
    return 0;
  }
};

// Obtener visitas globales (total de todos los tiempos)
export const getVisitasGlobales = async () => {
  try {
    const snapshot = await get(ref(db, 'visitas/total_global'));
    if (snapshot.exists()) {
      return snapshot.val() || 0;
    }
    return 0;
  } catch (error) {
    console.error('Error obteniendo visitas globales:', error);
    return 0;
  }
};

// Obtener visitas de un día específico
export const getVisitasPorDia = async (fecha) => {
  try {
    const snapshot = await get(ref(db, `visitas/${fecha}`));
    if (snapshot.exists()) {
      return snapshot.val().total || 0;
    }
    return 0;
  } catch (error) {
    console.error('Error obteniendo visitas:', error);
    return 0;
  }
};

// Obtener visitas de los últimos 7 días
export const getUltimos7Dias = async () => {
  try {
    const dias = [];
    const hoy = new Date();
    
    for (let i = 6; i >= 0; i--) {
      const fecha = new Date(hoy);
      fecha.setDate(fecha.getDate() - i);
      const fechaStr = fecha.toISOString().split('T')[0];
      const snapshot = await get(ref(db, `visitas/${fechaStr}`));
      const total = snapshot.exists() ? snapshot.val().total || 0 : 0;
      dias.push({
        fecha: fechaStr,
        total: total
      });
    }
    return dias;
  } catch (error) {
    console.error('Error obteniendo últimas visitas:', error);
    return [];
  }
};

export default db;